import React, { useState, useEffect } from 'react';
import { BarChart3, Package, TrendingUp, Calendar, Filter, Download, Users, DollarSign, Settings } from 'lucide-react';
import { usePopup } from '../hooks/usePopup';

const ReportesSimple = () => {
  const { showSuccess, showError, showWarning, showInfo } = usePopup();
  
  const [tipoReporte, setTipoReporte] = useState('ventas');
  // Fecha desde: hace 60 días para capturar más ventas
  const [fechaDesde, setFechaDesde] = useState(new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [fechaHasta, setFechaHasta] = useState(new Date().toISOString().split('T')[0]);
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generarReporte = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Obtener token del localStorage
      const token = localStorage.getItem('comercio_token');
      if (!token) {
        showError('Token no encontrado', 'Por favor, inicia sesión nuevamente.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        tipo: tipoReporte,
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        token: token
      });

      const response = await fetch(`/sf3/ventas/api/reportes_datos_reales.php?${params}`);
      const result = await response.json();

      if (result.success) {
        setDatos(result.data);
        showSuccess('Reporte Generado', `Reporte de ${tipoReporte} generado correctamente`);
      } else {
        // Mostrar popup personalizado de advertencia
        showWarning(
          'No hay datos', 
          result.message || 'No hay datos disponibles para el período seleccionado'
        );
        setError(result.message || 'No hay datos para este período');
      }
    } catch (err) {
      showError('Error de Conexión', 'No se pudo conectar con el servidor: ' + err.message);
      setError('Error de conexión: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportarDatos = () => {
    if (!datos) {
      showWarning('No hay datos', 'Primero genera un reporte para poder exportarlo');
      return;
    }
    
    try {
      const dataStr = JSON.stringify(datos, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reporte_${tipoReporte}_${fechaDesde}_${fechaHasta}.json`;
      link.click();
      
      showSuccess('Datos Exportados', `El reporte se ha descargado correctamente en formato JSON`);
    } catch (err) {
      showError('Error al Exportar', 'No se pudieron exportar los datos: ' + err.message);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '30px',
        borderRadius: '15px',
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '2.5rem' }}>📊 Reportes Simples</h1>
        <p style={{ margin: '0', fontSize: '1.2rem', opacity: 0.9 }}>Sistema de reportes directo y funcional</p>
      </div>

      {/* Filtros */}
      <div style={{
        background: 'white',
        padding: '25px',
        borderRadius: '15px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        marginBottom: '30px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Filter size={24} />
          Filtros de Reporte
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Tipo de Reporte</label>
            <select 
              value={tipoReporte} 
              onChange={(e) => setTipoReporte(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px'
              }}
            >
              <option value="ventas">📊 Reportes de Ventas</option>
              <option value="inventario">📦 Reportes de Inventario</option>
              <option value="productos">🛍️ Reportes de Productos</option>
              <option value="atencion">👥 Reportes de Atención</option>
              <option value="financiero">💰 Reportes Financieros</option>
              <option value="sistema">⚙️ Reportes del Sistema</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Fecha Desde</label>
            <input 
              type="date" 
              value={fechaDesde} 
              onChange={(e) => setFechaDesde(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Fecha Hasta</label>
            <input 
              type="date" 
              value={fechaHasta} 
              onChange={(e) => setFechaHasta(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px'
              }}
            />
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '15px' }}>
          <button 
            onClick={generarReporte}
            disabled={loading}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 25px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {loading ? '⏳' : '📊'} {loading ? 'Generando...' : 'Generar Reporte'}
          </button>
          
          {datos && (
            <button 
              onClick={exportarDatos}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 25px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Download size={16} />
              Exportar JSON
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #fecaca'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Datos */}
      {datos && (
        <div style={{
          background: 'white',
          padding: '25px',
          borderRadius: '15px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            {tipoReporte === 'ventas' ? <BarChart3 size={24} /> : 
             tipoReporte === 'inventario' ? <Package size={24} /> :
             tipoReporte === 'productos' ? <Package size={24} /> :
             tipoReporte === 'atencion' ? <Users size={24} /> :
             tipoReporte === 'financiero' ? <DollarSign size={24} /> :
             <Settings size={24} />}
            Reporte de {tipoReporte === 'ventas' ? 'Ventas' : 
                       tipoReporte === 'inventario' ? 'Inventario' :
                       tipoReporte === 'productos' ? 'Productos' :
                       tipoReporte === 'atencion' ? 'Atención' :
                       tipoReporte === 'financiero' ? 'Financiero' :
                       'Sistema'}
          </h3>
          
          {/* Resumen */}
          {datos.resumen && (
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#374151' }}>Resumen General</h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '15px' 
              }}>
                {Object.entries(datos.resumen).map(([key, value]) => (
                  <div key={key} style={{
                    background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                    padding: '15px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
                      {typeof value === 'number' ? value.toLocaleString() : value}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#6b7280', textTransform: 'capitalize' }}>
                      {key.replace(/_/g, ' ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tablas de datos */}
          {Object.entries(datos).filter(([key]) => key !== 'resumen').map(([key, value]) => (
            <div key={key} style={{ marginBottom: '30px' }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#374151', textTransform: 'capitalize' }}>
                {key.replace(/_/g, ' ')}
              </h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: 'white',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      {value.length > 0 && Object.keys(value[0]).map((header) => (
                        <th key={header} style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: 'bold',
                          color: '#374151',
                          borderBottom: '2px solid #e5e7eb',
                          textTransform: 'capitalize'
                        }}>
                          {header.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {value.map((row, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        {Object.values(row).map((cell, cellIndex) => (
                          <td key={cellIndex} style={{
                            padding: '12px',
                            color: '#374151'
                          }}>
                            {typeof cell === 'number' ? cell.toLocaleString() : cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportesSimple;
